import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useBlackFriday } from '../../contexts/BlackFridayContext';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100%);
  }
`;

const flash = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const sparkle = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
  100% { transform: scale(0) rotate(360deg); opacity: 0; }
`;

const smokeRise = keyframes`
  0% { transform: translate(-20%, 30%) scale(0.7); opacity: 0.2; }
  50% { transform: translate(10%, 0%) scale(1); opacity: 0.45; }
  100% { transform: translate(25%, -30%) scale(1.3); opacity: 0; }
`;

const BannerContainer = styled.div`
  position: fixed;
  top: 27px;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.3rem 0;
  text-align: center;
  z-index: 0;
  transform: translateY(-100%);
  animation: ${props => {
    if (props['data-show'] === 'true') return slideDown;
    if (props['data-hide'] === 'true') return slideUp;
    return 'none';
  }} 0.5s ease forwards;
  margin-top: 120px;

  @media (max-width: 768px) {
    margin-top: 120px;
    padding: 0.2rem 0;
    z-index: 00;
  }
`;

const UrgentBannerContainer = styled(BannerContainer)`
  background-color: ${props => props.theme.colors.accent};
  color: white;
`;

// Removed unused SoldOutBannerContainer to satisfy linter

const HalloweenBannerContainer = styled(BannerContainer)`
  background-color: #e55a00;
  color: white;
`;

const MagicalBannerContainer = styled(BannerContainer)`
  background-color: #101d40;
  color: white;
  position: fixed;
  top: 27px;
  left: 0;
  right: 0;
  padding: 0.3rem 0;
  text-align: center;
  z-index: 0;
  transform: translateY(-100%);
  animation: ${props => {
    if (props['data-show'] === 'true') return slideDown;
    if (props['data-hide'] === 'true') return slideUp;
    return 'none';
  }} 0.5s ease forwards;
  margin-top: 120px;

  @media (max-width: 768px) {
    margin-top: 120px;
    padding: 0.2rem 0;
    z-index: 00;
  }

  &::before,
  &::after,
  & span::before,
  & span::after {
    content: '';
    position: absolute;
    background: white;
    border-radius: 50%;
    pointer-events: none;
    animation: ${sparkle} 2s infinite;
  }

  &::before {
    width: 4px;
    height: 4px;
    top: 20%;
    left: 20%;
    animation-delay: 0.5s;
  }

  &::after {
    width: 3px;
    height: 3px;
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }

  & span::before {
    width: 5px;
    height: 5px;
    top: 40%;
    left: 30%;
    animation-delay: 0.2s;
  }

  & span::after {
    width: 3px;
    height: 3px;
    top: 70%;
    right: 30%;
    animation-delay: 1.5s;
  }
`;

const BlackFridayBannerContainer = styled(MagicalBannerContainer)`
  background: radial-gradient(circle at 20% 20%, rgba(247,198,84,0.25), transparent 60%),
    linear-gradient(120deg, #050505, #0f0f16 45%, #1c1522 100%);
  border-bottom: 1px solid rgba(247,198,84,0.25);
  box-shadow: 0 35px 80px rgba(0,0,0,0.6);

  &::before,
  &::after {
    animation: ${smokeRise} 18s infinite;
    background: radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(5,5,8,0) 60%);
    filter: blur(18px);
    opacity: 0.45;
  }

  &::before {
    width: 200px;
    height: 200px;
    top: 10%;
    left: 5%;
  }

  &::after {
    width: 260px;
    height: 260px;
    bottom: -10%;
    right: 10%;
    animation-delay: 6s;
  }
`;

const GreenBannerContainer = styled(BannerContainer)`
  background-color: #2e7d32;
  color: white;
`;

const BannerContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 500;
  animation: ${props => props['data-flash'] === 'true' ? flash : 'none'} 0.5s ease 3;

  &.black-friday-banner {
    flex-direction: column;
    gap: 0.4rem;
    text-align: center;
  }
`;

const BannerMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  strong {
    font-size: 1.05rem;
    color: #fff3d6;
  }

  span {
    font-size: 0.95rem;
    letter-spacing: 0.04em;
    opacity: 0.9;
  }
`;

const BannerLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.1rem 0.6rem;
  border: 1px solid white;
  border-radius: 15px;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background-color: white;
    color: ${props => props.theme.colors.accent};
  }
`;

const BannerExternalLink = styled.a`
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.1rem 0.6rem;
  border: 1px solid white;
  border-radius: 15px;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background-color: white;
    color: ${props => props.theme.colors.accent};
  }
`;

const AnnouncementBanner = () => {
  const [show, setShow] = useState(false);
  const [hide, setHide] = useState(false);
  const [flash, setFlash] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const location = useLocation();
  const { isActive: isBlackFriday, message: blackFridayMessage } = useBlackFriday();
  const isEventsPage = location.pathname.includes('/whats-on/events');
  const isWhatsOnPage = location.pathname === '/whats-on';
  const isEventDetailPage = location.pathname.includes('/event/');
  const isDayTicketsPage = location.pathname.includes('/day-tickets');
  const isHomePage = location.pathname === '/';
  const isChristmasPage = location.pathname.includes('/christmas');
  const isFarmShopPage = location.pathname.startsWith('/farm-shop') || location.pathname.includes('/retail-village/shops/farm-shop');
  const showFlowerShowMessages = isEventsPage || isWhatsOnPage || isEventDetailPage || isHomePage || isChristmasPage;

  // Removed unused date helpers to satisfy linter

  const isAfterAugust17th = () => {
    const cutoffDate = new Date('2025-08-17');
    const currentDate = new Date();
    return currentDate > cutoffDate;
  };

  const flowerShowMessages = [
    {
      text: "🎅 Search for Santa: New Dates Available - 22nd, 23rd & 24th December",
      type: "magical",
      duration: 5000,
      link: "/christmas/search-for-santa",
      buttonLabel: "Book Now"
    },
    {
      text: "Limited Tickets remain for Santa's Grotto",
      type: "magical",
      duration: 5000,
      link: "https://www.belvoircastle.com/christmas/santas-grotto",
      buttonLabel: "Book Now",
      isExternal: true
    },
    {
      text: "🎄 Opening Our Doors Wider Than Ever This Christmas — The More People You Add, The More You Save",
      type: "magical",
      duration: 5000,
      link: "/christmas/your-festive-visit",
      buttonLabel: "Discover More"
    },
    {
      text: "Your Castle, Your Crowd, Your Saving — Save with the Family Saver this Christmas",
      type: "green",
      duration: 5000,
      link: "/christmas/your-festive-visit",
      buttonLabel: "Save Now"
    }
  ];

  const membershipMessage = {
    text: "Explore Unlimited Access & Great Offers",
    type: "urgent",
    duration: 5000,
    link: "/memberships",
    buttonLabel: "Explore"
  };

  const farmShopMessage = {
    text: "Click & Collect Only - Deliveries Coming Soon",
    type: "urgent",
    duration: 5000,
    link: null,
    buttonLabel: null,
    noLink: true
  };

  const weddingShowcaseMessage = {
    text: "Your Dream Wedding Awaits - Book Our Wedding Showcase & Discover More",
    type: "urgent",
    duration: 5000,
    link: "https://www.belvoircastle.com/event/cG9zdDozOTk",
    buttonLabel: "Book Now",
    isExternal: true
  };

  const circusMessage = {
    text: "The Circus is returning to Belvoir this Spring - Tickets Available Soon",
    type: "urgent",
    duration: 5000,
    link: null,
    buttonLabel: null,
    noLink: true
  };

  const blackFridayTakeover = isBlackFriday ? {
    text: blackFridayMessage?.headline || "Black Friday: 20% Off Entry and Santa Tickets + Additional Family Saving",
    subtext: blackFridayMessage?.body || "Bring your visiting party to Belvoir Castle and watch 20% fall away automatically.",
    type: "blackFriday",
    duration: 8000,
    link: blackFridayMessage?.ctaLink || "/christmas/your-festive-visit",
    buttonLabel: blackFridayMessage?.ctaLabel || "Unlock 20% Off"
  } : null;

  // Get the current messages based on date and page
  const getCurrentMessages = () => {
    // Show wedding showcase message and circus message
    return [weddingShowcaseMessage, circusMessage];
    
    // Show farm shop message on farm shop pages when Black Friday is not active
    // if (isFarmShopPage && !blackFridayTakeover) {
    //   return [farmShopMessage];
    // }
    
    // Black Friday takes priority when active
    // if (blackFridayTakeover) {
    //   return [blackFridayTakeover];
    // }

    // const messages = [];

    // if (isDayTicketsPage) {
    //   return [...messages, membershipMessage];
    // }
    // if (showFlowerShowMessages) {
    //   let flowerMessages = flowerShowMessages;
      
    //   // Filter out expired messages
      
    //   if (isAfterAugust17th()) {
    //     flowerMessages = flowerMessages.filter(msg => msg.text !== "Vineyard Tour Now Available");
    //   }
      
    //   return [...messages, ...flowerMessages];
    // }
    // return messages;
  };

  const currentMessages = useMemo(() => getCurrentMessages(), [isDayTicketsPage, showFlowerShowMessages, isFarmShopPage, location.pathname, blackFridayTakeover]);

  useEffect(() => {
    if (currentMessages.length === 0) {
      return;
    }

    // Reset to first message when component mounts or messages change
    setMessageIndex(0);

    const rotateMessages = () => {
      if (currentMessages.length <= 1) return; // Don't rotate if only one message
      
      setHide(true);
      setTimeout(() => {
        setShow(false);
        setHide(false);
        setMessageIndex(prevIndex => {
          const nextIndex = (prevIndex + 1) % currentMessages.length;


          return nextIndex;
        });
        setTimeout(() => {
          setShow(true);
          setFlash(true);
          // Reset flash after animation completes
          setTimeout(() => {
            setFlash(false);
          }, 1500); // 0.5s * 3 iterations = 1.5s
        }, 100);
      }, 500);
    };

    // Initial show
    const initialTimer = setTimeout(() => {

      setShow(true);
      setHide(false);
      setFlash(true);
      // Reset flash after initial animation completes
      setTimeout(() => {
        setFlash(false);
      }, 1500); // 0.5s * 3 iterations = 1.5s
    }, 3000);

    // Start rotation only if there are multiple messages
    let rotationTimer;
    if (currentMessages.length > 1) {
      rotationTimer = setInterval(rotateMessages, 10000);
    }

    return () => {
      clearTimeout(initialTimer);
      if (rotationTimer) clearInterval(rotationTimer);
    };
  }, [isDayTicketsPage, showFlowerShowMessages, isFarmShopPage, currentMessages]);

  const getBannerContainer = () => {
    if (currentMessages.length === 0) {
      return BannerContainer;
    }

    const currentMessage = currentMessages[messageIndex] || currentMessages[0];

    if (currentMessage.type === 'blackFriday') {
      return BlackFridayBannerContainer;
    }

    if (isDayTicketsPage) {
      return UrgentBannerContainer;
    }

    // Check if this is the Halloween message
    if (currentMessage.text && (currentMessage.text.includes('Halloween') || currentMessage.text.includes('Fangula'))) {
      return HalloweenBannerContainer;
    }

    return currentMessage.type === "magical" ? MagicalBannerContainer : UrgentBannerContainer;
  };

  const BannerComponent = getBannerContainer();

  // Don't render anything if no messages to show
  if (currentMessages.length === 0) {
    return null;
  }

  const currentMessage = currentMessages[messageIndex] || currentMessages[0];

  return (
    <BannerComponent data-show={show.toString()} data-hide={hide.toString()}>
      <BannerContent 
        data-flash={flash.toString()}
        className={currentMessage.type === 'blackFriday' ? 'black-friday-banner' : undefined}
      >
        {currentMessage.type === 'blackFriday' ? (
          <BannerMessage>
            <strong>{currentMessage.text}</strong>
            {currentMessage.subtext && <span>{currentMessage.subtext}</span>}
          </BannerMessage>
        ) : (
          currentMessage.text
        )}
        {currentMessage.noLink ? null : (
          isDayTicketsPage && currentMessage === membershipMessage ? (
            <BannerLink to="/memberships">Explore</BannerLink>
          ) : currentMessage.isExternal ? (
            <BannerExternalLink href={currentMessage.link} target="_blank" rel="noopener noreferrer">
              {currentMessage.buttonLabel || "Read More"}
            </BannerExternalLink>
          ) : currentMessage.link ? (
            <BannerLink to={currentMessage.link}>
              {currentMessage.buttonLabel || "Book Now"}
            </BannerLink>
          ) : null
        )}
        {currentMessage.type === "magical" && <span />}
      </BannerContent>
    </BannerComponent>
  );
};

export default AnnouncementBanner; 